<script>    
    // Import App foundations (Svelte critical elements, Tailwind, etc.)
    // Svelte
    import { onMount } from 'svelte';
    import { fly } from 'svelte/transition';
    // Tailwind CSS
	import Tailwind from './screens/tailwind.svelte';
    // Firebase
    import firebaseConfig from "./env";
    import { initializeApp } from "firebase/app";
    import { getAdditionalUserInfo, getAuth, onAuthStateChanged } from "firebase/auth";
	import { signInWithPopup, GoogleAuthProvider, createUserWithEmailAndPassword, signOut } from "firebase/auth";
    import { getFirestore, collection, getDocs, addDoc } from "firebase/firestore";
    import { getFunctions, httpsCallable } from 'firebase/functions';

    // Import var from stores
    import { user } from './store/authStore';
    import { page, modal } from './store/nav';
    import { recurring } from "./store/recurring";

    // Import helper functions
    import { createId, nextDate } from "./js/helper";

    // Import db connection
    import { idb } from './db';

    // Import Screens
    import Nav from './screens/navBar.svelte';
    import Menu from './screens/menu.svelte';
    import NotFound from './screens/404.svelte';
    import Login from './screens/login.svelte';
    import Plan from './screens/plan.svelte';
	import Home from './screens/home.svelte';
    import Profile from './screens/profile.svelte';
    import List from './screens/list.svelte';
    import Items from './screens/list-items.svelte';
    import SelectStore from './screens/select-store.svelte';
    import SelectItemGeneric from './screens/select-item-generic.svelte';
    import SelectItemCategory from './screens/select-item-category.svelte';
    import Recurring from "./screens/recurring.svelte";
    import MyKitchen from "./screens/my-kitchen.svelte";
    import MyHome from "./screens/my-home.svelte";
    import StandaloneShop from "./screens/standalone-shop.svelte";

    // Import Modals
    import Modal from './modals/modal.svelte';
    import NewStore from './modals/new-store.svelte';
    import NewItem from './modals/new-item.svelte';
    import SelectCategory from './modals/select-category.svelte';
    import UpdateItem from './modals/update-item.svelte';
    import MenuListsItems from "./modals/menu-lists-items.svelte";
    import MenuListsRecurring from "./modals/menu-lists-recurring.svelte";
    import RecurringFilters from "./modals/recurring-filters.svelte";
    import UpdateRecurringItem from "./modals/update-recurring-item.svelte";
    import Message from "./modals/message.svelte";
    import OnlineFulfillment from "./modals/online-fulfillment.svelte";
    import UpdateUser from './modals/update-user.svelte';
    
    const pages = [
        {route: "/menu", component: Menu},
        {route: "/home", component: Home },
        {route: "/login", component: Login },
        {route: "/plan", component: Plan},
        {route: "/profile", component: Profile},
        {route: "/lists", component: List},
        {route: "/lists/items", component: Items},
        {route: "/lists/items/store", component: SelectStore},
        {route: "/lists/items/item-generic", component: SelectItemGeneric},
        {route: "/lists/items/item-category", component: SelectItemCategory},
        {route: "/lists/recurring", component: Recurring},
        {route: "/profile/my-kitchen", component: MyKitchen},
        {route: "/profile/my-home", component: MyHome},
        {route: "/standalone-shop", component: StandaloneShop},
    ];

    const modals = [
        {route: "/new-store", component: NewStore },
        {route: "/new-item", component: NewItem },
        {route: "/select-category", component: SelectCategory },
        {route: "/update-item", component: UpdateItem},
        {route: "/menu-lists-items", component: MenuListsItems},
        {route: "/menu-lists-recurring", component: MenuListsRecurring},
        {route: "/recurring-filters", component: RecurringFilters},
        {route: "/update-recurring-item", component: UpdateRecurringItem},
        {route: "/message", component: Message},
        {route: "/online-fulfillment", component: OnlineFulfillment},
        {route: "/update-user", component: UpdateUser},
    ];

    window.navigator.standalone;
    window.matchMedia('(display-mode: standalone)').matches;

    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        deferredPrompt = e;
        console.log(`Not installed`);
        alert(`BING!`);
        // Update UI notify the user they can add to home screen
        //btnAdd.style.display = 'block';
    });

    // Detects if device is on iOS 
    const isIos = () => {
        const userAgent = window.navigator.userAgent.toLowerCase();
        console.log(userAgent);
        return /iphone|ipad|ipod/.test( userAgent );
    }
    // Detects if device is in standalone mode
    const isInStandaloneMode = () => ('standalone' in window.navigator) && (window.navigator.standalone);

    // Checks if should display install popup notification:
    if (isIos() && !isInStandaloneMode()) {
        // offer app installation here
    }

    let component;
    let modalComponent;
    let props;

    if('serviceWorker' in navigator){
        navigator.serviceWorker.register('/service-worker.js')
            .then( ()=> console.log(`service worker registered`) )
            .catch( (err) => console.log(`[ERROR] Service worker failed to register`, err) )
    }

    const syncRecurringItems = async () => {
        // To prevent execution on every page load store a ts cookie in ls
        const today = new Date();
        const todayTS = Date.parse(today.toDateString()); // Return the beginning of the day then convert it in epoch
        if ( localStorage.getItem(`pc_last_recurring_item_sync`) != todayTS ) {
            localStorage.setItem(`pc_last_recurring_item_sync`, todayTS);

            // Check if there are any recurring items due for the next 3 days
            const pastQTS = new Date().setDate(today.getDate() - 90) ;
            const upcomingItems = await idb.getAllFromIndex.bt( 'pc_recurring', 'next', [ pastQTS, todayTS ]);
            //console.log(`[APP] be.task.syncRecurringItems >>> upcomingItems`,upcomingItems);

            // For each item -> Check if a list exists for the relevant store
            // Consolidate the list of stores to check
            /* { 
                storeUid : [
                    { items }    
                ]
            } */
            if (upcomingItems.length > 0) {
                let storeList = {};

                const reduce = async (item) => {
                    //console.log(item);
                    if (storeList.hasOwnProperty(item.storeUid) ) {
                        storeList[item.storeUid] = [...storeList[item.storeUid], item];
                    } else {
                        storeList[item.storeUid] = [item];
                    }  
                    //console.log(storeList);
                };

                for (const item of upcomingItems){
                    await reduce(item);
                }

                //console.log(`[APP] be.task.syncRecurringItems >>> storeList`,storeList);
                // For each store check if a list exists                
                Object.keys(storeList).forEach( async storeUid => {
                    const newListUid = createId.md();
                    const lists = await idb.getAllFromIndex.eq(`pc_lists`, `storeUid`, storeUid);
                    // If not create one
                    if ( lists.length === 0 ) {
                        const newList = {
                            "uid": newListUid,
                            "name":`${storeList[storeUid][0].storeName}`,
                            "date":"2022-05-19",
                            "status":"active",
                            "storeUid":`${storeUid}`,
                            "storeName":`${storeList[storeUid][0].storeName}`,
                            "storeLogo":`${storeUid}`,
                            "progress":0,
                            "lastUpdated":Date.now(),
                            "createdOn":Date.now()
                        };
                        await idb.add(`pc_lists`, newList);
                    }

                    // Check if an entry exists for the item
                    // consolidate all list of items into one bucket
                    const getAllItemsFromLists = async (list) => {
                        console.log(list);

                        items = [...items, ...await idb.getAllFromIndex.eq(`pc_items`, `listUid`, list.uid)];
                        console.log(items);
                    };

                    let items = [];

                    for (const list of lists){
                        await getAllItemsFromLists(list);
                    }

                    console.log(items);

                    const listUid = lists[0] ? lists[0].uid : newListUid;
                    storeList[storeUid].forEach( async item => {
                        const existingItem = items.find( i => ( i.productUid == item.productUid && i.cut == item.cut ));
                        console.log(existingItem);
                        // Set next date based on frequency
                        let daysToAdd = 7;
                        if ( item.frequency == "other w" ) {
                            daysToAdd = 15;
                        } else if ( item.frequency == "monthly" ) {
                            daysToAdd = 30;
                        } else if ( item.frequency == "quaterly" ) {
                            daysToAdd = 90;
                        }
                        // If not create one
                
                        if ( !existingItem ) {
                            const newItem = {
                                "uid":createId.uuid(),
                                "productUid":item.productUid,
                                "listUid": listUid,
                                "name":item.name,
                                "category":item.category,
                                "collected":false,
                                "status":"it_none",
                                "qty":1,
                                "unit":item.unit,
                                "recurring": item.frequency,
                                "recurringUid":item.uid,
                                "next":nextDate(daysToAdd),
                                "cut": item.cut,
                                "options":item.options,
                                "details":item.details
                            };

                            await idb.add(`pc_items`, newItem);
                            await recurring.init(item.uid);
                            await recurring.editSingleField(item.uid, `next`, nextDate(daysToAdd))   
                        }
                    });
                });
            }
        }
    };

    const be = {
        tasks: {
            run: async () => {
                await syncRecurringItems();
            }
        }
    };

    const parseRoute = (url) => {
        // If url is null => exit
        if ( !url ) return null;

        // Set route as first part of the url split
        const urlSplit = url.split('?');
        const nav = {
            route: urlSplit[0],
            props: {}
        };
        
        // If there is no query params to parse as props => exit
        if ( urlSplit.length < 2 ) return nav;
        
        // If there are query params split on = to set each key-value pair as a prop 
        urlSplit[1].split('&').forEach( kvpair => nav.props[kvpair.split('=')[0]] = kvpair.split('=')[1] );

        // Exit
        return nav;
    }

    page.subscribe( n => {
        // Runs background tasks at each navigation
        be.tasks.run();
        
        let nav = parseRoute(n.url);
        console.group(`Navigation: Screen`);console.log(nav);console.groupEnd();
        try {
            component = pages.find((p) => p.route === nav.route).component;
            props = nav.props;
        } catch (e) {
            component = NotFound;
        };
    });

    modal.subscribe( n => {
        let nav = parseRoute(n.url);
        console.group(`Navigation: Modal`);console.log(nav);console.groupEnd();
        try {
            modalComponent = modals.find((p) => p.route === nav.route).component;
            props = nav.props;
        } catch (e){ 
            console.log(`[ERROR] Parsing modal url failed`);
        }
    });

    onMount(async () => {
        console.log(`Prep Chef v 0.0.2`);        
        if ( window.location.href === "https://prepchef.app/#/standalone-shop" || window.location.href === "http://localhost:8080/#/standalone-shop") {
            page.route(`/standalone-shop`);
        } else {
            // Initialize Firebase
            const app = initializeApp(firebaseConfig);
            // Initialize Cloud Functions and get a reference to the service
            const fn = getFunctions(app);
            
            const addFamilyId = httpsCallable(fn, 'addFamilyId');

            const auth = getAuth();
            onAuthStateChanged(auth, userInfo => { 
                if (userInfo) { 
                    userInfo.getIdTokenResult().then(idTokenResult => {
                    const claims =idTokenResult.claims;

                    user.set({...userInfo, isLoggedIn: true, familyId: claims.familyId});
                    // console.log($user);
                })
                    // console.log(`creation: ${auth.currentUser.metadata.creationTime} | last: ${auth.currentUser.metadata.lastSignInTime}`);
                    if($user.email && !$user.familyId){
                    addFamilyId({ email: $user.email, familyId: 'test' })
                        .then((result) => {
                            const data = result.data;
                            const sanitizedMessage = data.text;
                            console.log(`Cloud fn triggered`);
                        });
                    }
                } else {
                    user.set({ isLoggedIn: false });
                    // console.log($user.isLoggedIn);
                    page.route('/login');
                }
            });
        }
    })

	async function getData(){
		const app = initializeApp(firebaseConfig);
		// Initialize Cloud Firestore and get a reference to the service
		const db = getFirestore(app);
		const querySnapshot = await getDocs(collection(db, "household"));
        querySnapshot.forEach((doc) => {
        	console.log(`${doc.id} => ${doc.data()}`);
        })
	}
		/*;*/
</script>


<Tailwind/>
<main class="m-0 p-0 fixed top-0 left-0 bottom-0 right-0 overflow-auto font-primary text-lg text-secondary bg-primary">
    <Modal {...props} open={$modal.open} on:close={modal.close}>
        <svelte:fragment slot="body">
            <svelte:component this={modalComponent} {...props} />
        </svelte:fragment>
    </Modal>

    <svelte:component this={component} {...props} />
</main>
